/**
 * Seedable deterministic PRNG (sfc32, 128-bit state, period ≈ 2^128).
 *
 * The state is a plain object of four uint32 numbers so it can live INSIDE SimState: it is cloned by `cloneInto`,
 * hashed by `hashState`, and rolled back with everything else. All operations use `| 0`, `>>> 0`, `Math.imul`
 * and shifts, so they are bit-identical everywhere.
 *
 * The sim must never call Math.random. Gameplay outcomes have no dice (VISION "Fairness"); the PRNG is for
 * cosmetic-but-simulated variety (e.g. which celebration plays) and for tests/loopback transports.
 */

export interface PrngState {
  a: number;
  b: number;
  c: number;
  d: number;
}

/** splitmix32 step used for seeding. */
const splitmix32 = (x: number): number => {
  let z = (x + 0x9e3779b9) | 0;
  z = Math.imul(z ^ (z >>> 16), 0x85ebca6b);
  z = Math.imul(z ^ (z >>> 13), 0xc2b2ae35);
  return (z ^ (z >>> 16)) >>> 0;
};

/** Create a fresh PRNG state from a 32-bit seed. */
export const createPrngState = (seed: number): PrngState => {
  const s: PrngState = { a: 0, b: 0, c: 0, d: 0 };
  seedPrng(s, seed);
  return s;
};

/** Re-seed an existing state in place (no allocation). */
export const seedPrng = (s: PrngState, seed: number): void => {
  let x = seed >>> 0;
  s.a = x = splitmix32(x);
  s.b = x = splitmix32(x);
  s.c = x = splitmix32(x);
  s.d = splitmix32(x);
  // Warm up.
  for (let i = 0; i < 12; i++) prngNextU32(s);
};

/** Next uint32 in [0, 2^32). Mutates `s`. */
export const prngNextU32 = (s: PrngState): number => {
  const t = (((s.a + s.b) | 0) + s.d) | 0;
  s.d = (s.d + 1) | 0;
  s.a = s.b ^ (s.b >>> 9);
  s.b = (s.c + (s.c << 3)) | 0;
  s.c = (s.c << 21) | (s.c >>> 11);
  s.c = (s.c + t) | 0;
  // Keep stored values canonical (signed int32) so hashes are stable.
  return t >>> 0;
};

/** Float in [0, 1). */
export const prngFloat = (s: PrngState): number => prngNextU32(s) / 4294967296;
/** Float in [lo, hi). */
export const prngRange = (s: PrngState, lo: number, hi: number): number => lo + (hi - lo) * prngFloat(s);
/** Integer in [0, n). n must be ≥ 1. */
export const prngInt = (s: PrngState, n: number): number => Math.floor(prngFloat(s) * n);
/** true with probability p. */
export const prngChance = (s: PrngState, p: number): boolean => prngFloat(s) < p;

export const copyPrngState = (dst: PrngState, src: PrngState): void => {
  dst.a = src.a;
  dst.b = src.b;
  dst.c = src.c;
  dst.d = src.d;
};

/** Convenience class for non-sim code (loopback transport, tests, AI personality). Not for SimState. */
export class Prng {
  readonly state: PrngState;
  constructor(seed: number) {
    this.state = createPrngState(seed);
  }
  nextU32(): number {
    return prngNextU32(this.state);
  }
  next(): number {
    return prngFloat(this.state);
  }
  range(lo: number, hi: number): number {
    return prngRange(this.state, lo, hi);
  }
  int(n: number): number {
    return prngInt(this.state, n);
  }
  chance(p: number): boolean {
    return prngChance(this.state, p);
  }
}

/** A random 32-bit match seed for NON-sim code (server/lobby). Never call inside the sim. */
export const randomSeed = (): number => (Math.random() * 4294967296) >>> 0;
